import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { Calendar, MapPin, AlertTriangle, MessageSquare, Volume2, Clock, AlertOctagon, RefreshCw } from 'lucide-react';

interface OrderItem {
  productId: number;
  productName: string;
  isProduced: boolean;
  status: string;
  colorHex: string;
}

interface Order {
  id: number;
  number: string | number;
  customerName: string;
  departure: string;
  city: string;
  isPriority: boolean;
  isEmergence: boolean;
  createdAt: string;
  dayOfWeek: string;
  statusName: string;
  customStatus?: string;
  items: OrderItem[];
}

interface TvMessage {
  id: number;
  type: 'TEXT' | 'IMAGE';
  title?: string;
  body?: string;
  imageUrl?: string;
  daysOfWeek?: string[];
  instantExhibition: boolean;
  isActive: boolean;
  classification: 'NORMAL' | 'URGENTE' | 'EMERGENCIA';
}

interface ProjectorConfig {
  dateFilter: 'CURRENT_MONTH' | 'CURRENT_MONTH_NEXT_FORTNIGHT' | 'CURRENT_MONTH_NEXT_MONTH';
  pageSize: number;
  slideInterval: number;
}

function formatBrazilianDate(dateStr: string): string {
  if (!dateStr) return '';
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
}

interface Slide {
  id: string;
  type: 'ORDERS_PAGE' | 'TV_MESSAGE';
  pageIndex?: number;
  totalPages?: number;
  orders?: Order[];
  message?: TvMessage;
  duration: number;
}

// Parse dates formatted as YYYY-MM-DD or DD/MM/YYYY
function parseOrderDate(dateStr: string): Date | null {
  if (!dateStr) return null;
  const cleanStr = dateStr.trim();
  if (cleanStr.includes('-')) {
    const parts = cleanStr.split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
        return new Date(year, month, day);
      }
    }
  }
  if (cleanStr.includes('/')) {
    const parts = cleanStr.split('/');
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      let year = parseInt(parts[2], 10);
      if (year < 100) {
        year += 2000;
      }
      if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
        return new Date(year, month, day);
      }
    }
  }
  return null;
}

export default function ProjectorView() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [messages, setMessages] = useState<TvMessage[]>([]);
  const [config, setConfig] = useState<ProjectorConfig>({
    dateFilter: 'CURRENT_MONTH_NEXT_FORTNIGHT',
    pageSize: 12,
    slideInterval: 8
  });
  const [loading, setLoading] = useState(true);

  const [slides, setSlides] = useState<Slide[]>([]);
  const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(0);
  const [secondsLeft, setSecondsLeft] = useState<number>(8);

  const [currentTime, setCurrentTime] = useState(new Date());

  // Real-time ticking Clock
  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Fetch production data and system configuration
  const fetchData = async () => {
    try {
      const [ordersRes, messagesRes, configRes] = await Promise.all([
        api.get<Order[]>('/orders'),
        api.get<TvMessage[]>('/tv-messages'),
        api.get<ProjectorConfig>('/projector-config').catch(() => ({ data: null }))
      ]);

      const fetchedOrders = ordersRes.data || [];
      const fetchedMessages = messagesRes.data || [];
      const fetchedConfig = configRes.data || {
        dateFilter: 'CURRENT_MONTH_NEXT_FORTNIGHT',
        pageSize: 12,
        slideInterval: 8
      };

      setOrders(fetchedOrders);
      setMessages(fetchedMessages);
      setConfig(fetchedConfig);

      // Re-generate slide deck dynamically
      const activeSlides = buildSlidesDeck(fetchedOrders, fetchedMessages, fetchedConfig);
      setSlides(activeSlides);

    } catch (error) {
      console.error('Error fetching projector information', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Poll the server state every 10 seconds for real-time update matching
    const pollInterval = setInterval(fetchData, 10000);
    return () => clearInterval(pollInterval);
  }, []);

  // Sync slide timer on index change or list length changes
  useEffect(() => {
    if (slides.length > 0) {
      const idx = currentSlideIndex < slides.length ? currentSlideIndex : 0;
      setSecondsLeft(slides[idx].duration);
    }
  }, [currentSlideIndex, slides.length]);

  // Handle countdown loop and incremental switches
  useEffect(() => {
    const interval = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          if (slides.length > 0) {
            const nextIdx = (currentSlideIndex + 1) % slides.length;
            setCurrentSlideIndex(nextIdx);
            return slides[nextIdx].duration;
          }
          return 8;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [currentSlideIndex, slides]);

  // Generate sequence of slides
  const buildSlidesDeck = (ordersList: Order[], messagesList: TvMessage[], cfg: ProjectorConfig) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // 1. Exclude finalized orders completely because "pedidos finalizados não importam"
    let filteredOrders = ordersList.filter(order => {
      const statusName = (order.statusName || '').toLowerCase();
      return !statusName.includes('finalizado');
    });

    // 2. Sorting:
    // Sorted mainly by departure date ascending (closest/oldest on top), ensuring overdue/shorter deadlines are absolute top
    // Secondary criteria: emergency (score 2), priority (score 1), normal (score 0)
    filteredOrders.sort((a, b) => {
      const dateA = parseOrderDate(a.departure);
      const dateB = parseOrderDate(b.departure);
      
      if (dateA && dateB) {
        if (dateA.getTime() !== dateB.getTime()) {
          return dateA.getTime() - dateB.getTime();
        }
      } else if (dateA) {
        return -1;
      } else if (dateB) {
        return 1;
      }

      const scoreA = a.isEmergence ? 2 : (a.isPriority ? 1 : 0);
      const scoreB = b.isEmergence ? 2 : (b.isPriority ? 1 : 0);
      if (scoreA !== scoreB) {
        return scoreB - scoreA;
      }
      return String(a.number).localeCompare(String(b.number), undefined, { numeric: true, sensitivity: 'base' });
    });

    // 3. Filter orders in target duration range
    const todayYear = today.getFullYear();
    const todayMonth = today.getMonth();

    const endOfCurrentMonth = new Date(todayYear, todayMonth + 1, 0, 23, 59, 59);
    const endOfNextFortnight = new Date(todayYear, todayMonth + 1, 15, 23, 59, 59);
    const endOfNextMonth = new Date(todayYear, todayMonth + 2, 0, 23, 59, 59);

    filteredOrders = filteredOrders.filter(order => {
      const orderDate = parseOrderDate(order.departure);
      if (!orderDate) return true;

      // Always include past/overdue dates: "os pedidos com data vencida importam muito, porque mostram que existem atrazos de produção."
      if (orderDate < today) {
        return true;
      }

      // Filter by dynamic configurations
      if (cfg.dateFilter === 'CURRENT_MONTH') {
        return orderDate <= endOfCurrentMonth;
      } else if (cfg.dateFilter === 'CURRENT_MONTH_NEXT_FORTNIGHT') {
        return orderDate <= endOfNextFortnight;
      } else if (cfg.dateFilter === 'CURRENT_MONTH_NEXT_MONTH') {
        return orderDate <= endOfNextMonth;
      }
      return true;
    });

    const deck: Slide[] = [];
    const pageSize = cfg.pageSize || 12;
    const totalPages = Math.ceil(filteredOrders.length / pageSize);

    // Break orders list into pages
    if (totalPages > 0) {
      for (let i = 0; i < totalPages; i++) {
        deck.push({
          id: `orders-page-${i}`,
          type: 'ORDERS_PAGE',
          pageIndex: i,
          totalPages: totalPages,
          orders: filteredOrders.slice(i * pageSize, (i + 1) * pageSize),
          duration: cfg.slideInterval || 8
        });
      }
    } else {
      deck.push({
        id: 'orders-empty',
        type: 'ORDERS_PAGE',
        pageIndex: 0,
        totalPages: 1,
        orders: [],
        duration: cfg.slideInterval || 8
      });
    }

    // 4. Gather active scheduled announcements
    const todayNum = new Date().getDay();
    const daysMap = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
    const todayName = daysMap[todayNum];

    const activeMessages = messagesList.filter(msg => {
      if (!msg.isActive) return false;
      if (msg.instantExhibition) return true;
      if (!msg.daysOfWeek || msg.daysOfWeek.length === 0) return true;

      return msg.daysOfWeek.some(day =>
        day.toLowerCase() === todayName.toLowerCase() ||
        day.replace('-Feira', '-feira').toLowerCase() === todayName.replace('-Feira', '-feira').toLowerCase()
      );
    });

    const getMsgDuration = (msg: TvMessage) => {
      if (msg.classification === 'EMERGENCIA') return 25;
      if (msg.classification === 'URGENTE') return 15;
      return 8;
    };

    activeMessages.forEach((msg, idx) => {
      deck.push({
        id: `msg-${msg.id}-${idx}`,
        type: 'TV_MESSAGE',
        message: msg,
        duration: getMsgDuration(msg)
      });
    });

    return deck;
  };

  const getFilterLabel = (f: string) => {
    switch (f) {
      case 'CURRENT_MONTH':
        return 'Mês Atual';
      case 'CURRENT_MONTH_NEXT_FORTNIGHT':
        return 'Mês Atual + Próxima Quinzena';
      case 'CURRENT_MONTH_NEXT_MONTH':
        return 'Mês Atual + Próximo Mês';
      default:
        return 'Mês Atual + Próxima Quinzena';
    }
  };

  const formatClock = (d: Date) => {
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatDateLong = (d: Date) => {
    const weekdays = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'];
    const formattedDate = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    return `${formattedDate} - ${weekdays[d.getDay()]}`;
  };

  const isDateOverdue = (dateStr: string) => {
    const parsed = parseOrderDate(dateStr);
    if (!parsed) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return parsed < today;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111] text-white flex flex-col items-center justify-center font-sans">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-sm tracking-widest font-mono text-zinc-400 uppercase">Sincronizando tela de monitoramento...</p>
        </div>
      </div>
    );
  }

  const activeSlide = slides[currentSlideIndex] || slides[0];

  return (
    <div className="min-h-screen bg-[#0b0b0c] text-[#dedebc] font-sans flex flex-col justify-between overflow-hidden">
      
      {/* ⚡ TINY HEADER (Minimizado para máximo ganho de espaço de visualização na fábrica) */}
      <header className="bg-[#121214] px-6 py-2 border-b border-zinc-900 flex justify-between items-center shadow-md">
        <div className="flex items-center gap-3">
          <div className="bg-amber-500 text-black font-extrabold px-1.5 py-0.5 rounded text-[10px] tracking-wider leading-none">
            MTC
          </div>
          <div>
            <h1 className="text-sm font-black tracking-wider text-amber-500 flex items-center gap-1.5 uppercase m-0 leading-none">
              Controle Geral de Produção
            </h1>
            <p className="text-[9px] text-zinc-500 font-mono tracking-wider uppercase m-0 mt-0.5">
              Filtro ativo: <span className="text-zinc-300 font-semibold">{getFilterLabel(config.dateFilter)}</span>
            </p>
          </div>
        </div>

        {/* Dynamic slide progress state indicator & countdown badge */}
        <div className="flex items-center gap-3">
          <div className="bg-amber-500/10 border border-amber-500/30 text-amber-400 px-3 py-1 rounded flex items-center gap-2 font-mono text-xs">
            <RefreshCw className="w-3.5 h-3.5 text-amber-500 animate-spin" />
            <span>Próximo slide em: <strong className="text-white font-extrabold">{secondsLeft}s</strong></span>
          </div>

          {activeSlide && activeSlide.type === 'ORDERS_PAGE' && (
            <div className="hidden md:flex items-center gap-2 bg-zinc-900 border border-zinc-800 px-3 py-1 rounded text-xs text-zinc-400">
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
              <span className="font-mono font-semibold">
                Pág. {(activeSlide.pageIndex ?? 0) + 1} de {activeSlide.totalPages ?? 1}
              </span>
            </div>
          )}

          <div className="bg-zinc-950 border border-zinc-800 px-3 py-1 rounded flex items-center gap-2 font-mono text-xs">
            <Clock className="text-amber-500 w-3.5 h-3.5" />
            <span className="text-white font-bold tracking-widest leading-none">{formatClock(currentTime)}</span>
          </div>

          <div className="text-right">
            <p className="text-[11px] font-semibold text-zinc-300 capitalize leading-none m-0">
              {formatDateLong(currentTime)}
            </p>
          </div>
        </div>
      </header>

      {/* MAIN CONTAINER */}
      <main className="flex-1 p-3 overflow-hidden flex flex-col justify-stretch">
        
        {activeSlide && activeSlide.type === 'ORDERS_PAGE' ? (
          <div className="w-full flex-1 flex flex-col animate-fade-in justify-between">
            
            {/* 📊 SQUEEZED SPREADSHEET VIEW (Com fontes maiores e mais legíveis à distância) */}
            <div className="flex-1 overflow-hidden border border-zinc-800 bg-[#121214] rounded shadow-xl flex flex-col">
              <div className="overflow-y-auto flex-1">
                <table className="w-full text-left border-collapse table-fixed">
                  <thead>
                    <tr className="bg-zinc-900/90 border-b border-zinc-800 text-zinc-300 text-sm font-bold uppercase tracking-wider sticky top-0 z-10">
                      <th className="py-3.5 px-4 font-mono text-center w-48">Nº PEDIDO</th>
                      <th className="py-3.5 px-5 w-1/3">CLIENTE</th>
                      <th className="py-3.5 px-5">CIDADE</th>
                      <th className="py-3.5 px-4 text-center w-40">DATA PREVISTA</th>
                      <th className="py-3.5 px-4 w-44">DIA DA SEMANA</th>
                      <th className="py-3.5 px-5 text-center w-56">STATUS GERAL</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-900/90">
                    {activeSlide.orders && activeSlide.orders.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-12 text-center text-zinc-500 font-mono uppercase text-sm">
                          Nenhum pedido pendente nesta quinzena / classificação de data.
                        </td>
                      </tr>
                    ) : (
                      activeSlide.orders && activeSlide.orders.map((order, idx) => {
                        const isOverdue = isDateOverdue(order.departure);
                        
                        return (
                          <tr 
                            key={order.id} 
                            style={{ height: '58px' }}
                            className={`transition-colors border-zinc-900 ${
                              order.isEmergence 
                                ? 'bg-rose-950/40 text-rose-100 hover:bg-rose-950/60' 
                                : order.isPriority 
                                  ? 'bg-amber-950/25 text-amber-100 hover:bg-amber-950/40' 
                                  : isOverdue 
                                    ? 'bg-zinc-900/60 text-zinc-100 hover:bg-zinc-850'
                                    : (idx % 2 === 0 
                                        ? 'bg-[#141416]/50 text-zinc-300 hover:bg-[#1a1a1c]' 
                                        : 'bg-[#222225]/70 text-zinc-300 hover:bg-[#28282b]')
                            }`}
                          >
                            {/* Number cell */}
                            <td className="py-2 px-4 font-mono text-center font-black text-amber-500 text-lg border-r border-zinc-900/40 whitespace-nowrap overflow-hidden text-ellipsis">
                              {order.number}
                            </td>

                            {/* Customer cell */}
                            <td className="py-2 px-5 tracking-wide whitespace-nowrap overflow-hidden text-ellipsis">
                              <span className="flex items-center gap-2">
                                <span className={`font-black text-lg uppercase ${order.isEmergence ? 'text-rose-400' : order.isPriority ? 'text-amber-400' : 'text-zinc-100'}`}>
                                  {order.customerName}
                                </span>
                                {order.isEmergence && (
                                  <span className="text-[10px] font-bold bg-rose-600 text-white px-2 py-0.5 rounded uppercase animate-pulse leading-tight shrink-0">
                                    Emergência
                                  </span>
                                )}
                                {order.isPriority && !order.isEmergence && (
                                  <span className="text-[10px] font-semibold bg-amber-500 text-black px-2 py-0.5 rounded uppercase leading-tight shrink-0">
                                    Prioridade
                                  </span>
                                )}
                              </span>
                            </td>

                            {/* City cell */}
                            <td className="py-2 px-5 text-zinc-300 text-sm uppercase font-semibold whitespace-nowrap overflow-hidden text-ellipsis">
                              <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-zinc-500 shrink-0" />
                                <span className="truncate">{order.city}</span>
                              </div>
                            </td>

                            {/* Departure cell */}
                            <td className={`py-2 px-4 text-center text-base font-mono font-black whitespace-nowrap ${isOverdue ? 'text-rose-400 animate-pulse bg-rose-900/10' : 'text-zinc-200'}`}>
                              <span className="flex items-center justify-center gap-1.5">
                                {isOverdue && <AlertOctagon className="w-4 h-4 text-rose-500 shrink-0" />}
                                {formatBrazilianDate(order.departure)}
                              </span>
                            </td>

                            {/* Day of Week cell */}
                            <td className="py-2 px-4 text-zinc-400 text-sm font-medium capitalize whitespace-nowrap overflow-hidden text-ellipsis">
                              {order.dayOfWeek || 'Segunda-feira'}
                            </td>

                            {/* Status badge cell */}
                            <td className="py-2 px-5 text-center border-l border-zinc-900/40">
                              <span 
                                className="px-4 py-1.5 rounded text-xs font-black tracking-wider inline-block text-white uppercase shadow-md truncate max-w-full"
                                style={{
                                  backgroundColor: 
                                    order.statusName.includes('Corte') ? '#2e66cc' : 
                                    order.statusName.includes('Dobra') ? '#e67300' : 
                                    order.statusName.includes('Solda') ? '#6f42c1' :
                                    order.statusName.includes('Acabamento') ? '#20c997' :
                                    order.statusName.includes('Instalação') ? '#c49102' : '#55555d'
                                }}
                              >
                                {order.statusName}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Micro timer status bar underneath sheet */}
            <div className="mt-2 flex justify-between items-center px-3 py-1.5 bg-zinc-950 border border-zinc-900/80 rounded-md text-zinc-500 text-xs font-mono select-none shrink-0">
              <div className="flex items-center gap-2">
                <RefreshCw className="w-3.5 h-3.5 text-zinc-600 animate-spin" />
                <span>Próxima atualização em {secondsLeft} segundos...</span>
              </div>
              <div className="flex items-center gap-3">
                <span>Total de pedidos ativos no monitor: <strong className="text-zinc-300 font-bold">{orders.filter(o => !o.statusName.toLowerCase().includes('finalizado')).length}</strong></span>
                {slides.length > 1 && (
                  <span className="text-amber-500 font-semibold">
                    Slide {currentSlideIndex + 1} / {slides.length}
                  </span>
                )}
              </div>
            </div>

          </div>
        ) : (
          /* EMBEDDED MESSAGE DESIGN MODE - Staggering announcement displays spanning full screen layout */
          activeSlide && activeSlide.message && (
            <div className="flex-1 w-full flex flex-col justify-stretch animate-fade-in">
              
              {/* Status Header for Message classification */}
              <div className={`w-full text-center px-6 py-3.5 rounded-t-lg font-black tracking-widest text-[#121214] text-base uppercase flex items-center justify-center gap-3 shrink-0 ${
                activeSlide.message.classification === 'EMERGENCIA' 
                  ? 'bg-rose-600 text-white animate-pulse'
                  : activeSlide.message.classification === 'URGENTE'
                    ? 'bg-amber-500 text-black'
                    : 'bg-zinc-300 text-zinc-900'
              }`}>
                {activeSlide.message.classification === 'EMERGENCIA' && <AlertTriangle className="w-5.5 h-5.5 animate-spin text-white" />}
                {activeSlide.message.classification === 'URGENTE' && <Volume2 className="w-5.5 h-5.5 animate-bounce text-black" />}
                <span className="font-extrabold tracking-wider">
                  {activeSlide.message.classification === 'EMERGENCIA' ? 'COMUNICADO DE EMERGÊNCIA' : 
                   activeSlide.message.classification === 'URGENTE' ? 'AVISO CONSTANTE DA DIREÇÃO' : 
                   'RECADO DA FÁBRICA / AVISO GERAL'}
                </span>
              </div>

              {/* Main Message Body Display */}
              {activeSlide.message.type === 'FULL_SCREEN_IMAGE' ? (
                <div className={`w-full flex-1 bg-[#121214] rounded-b-lg border border-t-0 shadow-2xl flex items-center justify-center overflow-hidden p-2 relative ${
                  activeSlide.message.classification === 'EMERGENCIA' ? 'border-rose-600' :
                  activeSlide.message.classification === 'URGENTE' ? 'border-amber-500' : 'border-zinc-800'
                }`}>
                  <img 
                    src={activeSlide.message.imageUrl} 
                    alt={activeSlide.message.title || 'Comunicado'}
                    className="w-full h-full object-contain rounded-b-lg max-h-[75vh]"
                    referrerPolicy="no-referrer"
                  />
                </div>
              ) : (
                <div className={`w-full flex-1 bg-[#121214] rounded-b-lg border border-t-0 p-12 shadow-2xl flex flex-col md:flex-row gap-8 items-center justify-between ${
                  activeSlide.message.classification === 'EMERGENCIA' ? 'border-rose-600' :
                  activeSlide.message.classification === 'URGENTE' ? 'border-amber-500' : 'border-zinc-800'
                }`}>
                  
                  <div className="flex-1 text-left">
                    <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white mb-6 uppercase">
                      {activeSlide.message.title}
                    </h2>
                    
                    <p className="text-xl md:text-2xl text-zinc-300 uppercase leading-relaxed font-normal">
                      {activeSlide.message.body}
                    </p>

                    {activeSlide.message.daysOfWeek && activeSlide.message.daysOfWeek.length > 0 && (
                      <div className="mt-8 flex items-center gap-2 text-zinc-500 font-mono text-sm">
                        <Calendar className="w-4 h-4" />
                        <span>Exibição programada: {activeSlide.message.daysOfWeek.join(', ')}</span>
                      </div>
                    )}
                  </div>

                  {activeSlide.message.type === 'IMAGE' && activeSlide.message.imageUrl && (
                    <div className="relative w-full md:w-[450px] h-64 md:h-[350px] rounded overflow-hidden border border-zinc-800 shadow-md shrink-0">
                      <img 
                        src={activeSlide.message.imageUrl} 
                        alt={activeSlide.message.title}
                        className="w-full h-full object-cover"
                        referrerPolicy="no-referrer"
                      />
                    </div>
                  )}
                </div>
              )}

            </div>
          )
        )}
      </main>

    </div>
  );
}
